import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import usePropertyContactInfo from "../../hooks/usePropertyContactInfo";
import IContactInfo, { IContactPerson } from "../../interfaces/IContactInfo";
import IProperty from "../../interfaces/IProperty";
import { formatPhone } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import FieldRow from "../FieldRow";
import Spinner from "../spinners/Spinner";
import styles from "./PropertyContactInfoPanel.module.css";

function PropertyContactInfoPanel({ property }: IPropertyContactInfoPanelProps) {
    const [expanded, setExpanded] = useState(false);
    const { contactInfo, isLoading, isError, fetchContactInfo } = usePropertyContactInfo(property);

    const handleChange = (isExpanded: boolean) => {
        setExpanded(isExpanded);
        if (isExpanded)
            fetchContactInfo();
    };

    return (
        <Accordion disableGutters expanded={expanded} onChange={(_, isExpanded) => handleChange(isExpanded)} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                <div className={commonStyles.header}>Contact Information</div>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                {isLoading && <Spinner />}
                {!isLoading && !isError && contactInfo && <ContactInfoContent contactInfo={contactInfo} />}
            </AccordionDetails>
        </Accordion>
    );
}

function ContactInfoContent({ contactInfo }: { contactInfo: IContactInfo }) {
    const hasData = !!(contactInfo.propertyAddress || contactInfo.mailingAddress || contactInfo.owners.length || contactInfo.persons.length);
    if (!hasData)
        return <div className={commonStyles.key}>Not enough data available</div>;

    return (
        <>
            <FieldRow label="PROPERTY ADDRESS">{contactInfo.propertyAddress}</FieldRow>
            <FieldRow label="MAILING ADDRESS">{contactInfo.mailingAddress}</FieldRow>
            <FieldRow label="OWNERS">{contactInfo.owners.length ? contactInfo.owners.join(", ") : null}</FieldRow>
            {contactInfo.persons.map((person, index) => <ContactPerson key={index} index={index + 1} person={person} />)}
        </>
    );
}

function ContactPerson({ index, person }: { index: number; person: IContactPerson }) {
    const details = [person.dob && `Dob: ${person.dob}`, person.deceased && "deceased"].filter(Boolean).join(", ");

    return (
        <div className={styles.person}>
            <div className={styles.personName}>
                {index}. {person.name || "Unknown"}{details && ` (${details})`}
            </div>
            <FieldRow label="POSSIBLE ADDRESSES">
                {!!person.addresses.length && person.addresses.map((address, i) => <div key={i}>{address}</div>)}
            </FieldRow>
            <FieldRow label="POSSIBLE PHONE NUMBERS">
                {!!person.phones.length && person.phones.map((phone, i) =>
                    <div key={i}>
                        {phone.type && `${phone.type} / `}{formatPhone(phone.number)}
                        {phone.reachable && <CheckIcon className={styles.check} />}
                    </div>
                )}
            </FieldRow>
            <FieldRow label="POSSIBLE EMAIL ADDRESSES">
                {!!person.emails.length && person.emails.map((email, i) => <div key={i}>{email}</div>)}
            </FieldRow>
        </div>
    );
}

interface IPropertyContactInfoPanelProps {
    property: IProperty;
}

const accordionSx = { boxShadow: "none", background: "transparent", color: "var(--text-color)", "&:before": { display: "none" } } as const;

const summarySx = {
    minHeight: 36,
    p: 0,
    "&.Mui-expanded": { minHeight: 36 },
    "& .MuiAccordionSummary-content": { my: 0 },
} as const;

export default observer(PropertyContactInfoPanel);
